import React, { useEffect, useState } from "react";
import { format } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { parseYMDLocal, formatYMD, dayOverlapsRanges, dayIsHostUnavailable, startOfDay } from "../lib/dateLocal";

/**
 * Reusable single-date picker. Uses local-time parsing (no UTC drift) and
 * supports `bookedRanges` to grey-out days that conflict with existing bookings.
 *
 * Props:
 *   value: "YYYY-MM-DD" or empty
 *   onChange: (newValue: string) => void
 *   minDate: "YYYY-MM-DD" — earliest selectable. If the current value is before
 *     a newly-set minDate, it auto-snaps to minDate so end-before-start never sticks.
 *   bookedRanges: [{ start, end }]  // overlapping days disabled
 *   calendarMode / calendarDays — host blocked/rentable days (KAN-126)
 *   listingPauses — scheduled pause windows that override rentable days (KAN-193)
 */
export default function DatePicker({
  value,
  onChange,
  label,
  placeholder = "Select a date",
  disabled,
  minDate,
  bookedRanges,
  calendarMode = "block_unavailable",
  calendarDays,
  listingPauses,
  testid,
  helperText,
}) {
  const [open, setOpen] = useState(false);
  const date = parseYMDLocal(value);
  const handleSelect = (d) => {
    if (!d) { onChange?.(""); return; }
    onChange?.(formatYMD(d));
    setOpen(false);
  };
  const minD = parseYMDLocal(minDate);
  const isDisabled = (d) => {
    // Always disallow dates earlier than today.
    if (startOfDay(d).getTime() < startOfDay(new Date()).getTime()) return true;
    if (minD && startOfDay(d).getTime() < startOfDay(minD).getTime()) return true;
    if (dayOverlapsRanges(d, bookedRanges)) return true;
    if (dayIsHostUnavailable(d, calendarMode, calendarDays, listingPauses)) return true;
    return false;
  };

  // Auto-correct: if current value is before minDate, snap to minDate.
  useEffect(() => {
    if (!value || !minD || !date) return;
    if (startOfDay(date).getTime() < startOfDay(minD).getTime()) {
      onChange?.(minDate);
    }
  }, [minDate]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      {label && <label className="text-xs text-zinc-500 block mb-1.5 flex items-center gap-1.5"><CalendarIcon size={12} /> {label}</label>}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            className="zc-input h-11 px-3 w-full min-w-0 text-sm text-left flex items-center justify-between gap-2"
            data-testid={testid}
          >
            <span className={value ? "truncate" : "text-[var(--zc-text-3)] truncate"}>
              {date ? format(date, "dd/MM/yyyy") : placeholder}
            </span>
            <CalendarIcon size={14} className="text-[var(--zc-text-3)]" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          className="w-auto p-0 bg-white border border-[var(--zc-border)] z-[100]"
          align="start"
          sideOffset={6}
        >
          <Calendar
            mode="single"
            selected={date}
            onSelect={handleSelect}
            disabled={isDisabled}
            initialFocus
          />
        </PopoverContent>
      </Popover>
      {helperText && <div className="zc-hint mt-1.5">{helperText}</div>}
    </div>
  );
}
